import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const tiers = [
  {
    name: 'Starter',
    price: '$100',
    cadence: '/mo',
    blurb:
      'For founders and small teams getting serious about social. Everything to launch a channel that compounds.',
    features: [
      'Up to 2 channels',
      'AI content + scheduling',
      '1 paid ads platform',
      'Weekly AI reports',
      'Email support',
    ],
    cta: 'Start with Starter',
    highlighted: false,
  },
  {
    name: 'Growth',
    price: '$500',
    cadence: '/mo',
    blurb:
      'The most chosen plan. Multi-channel execution, paid + organic, with a senior strategist in your corner.',
    features: [
      'Up to 5 channels',
      'AI creative variants + A/B',
      'Paid ads on Meta, Google, TikTok',
      'Audience intelligence',
      'Dedicated strategist',
      'Priority support',
    ],
    cta: 'Get Started',
    highlighted: true,
  },
  {
    name: 'Scale',
    price: 'Custom',
    cadence: '',
    blurb:
      'For brands and agencies that need full-stack ops, white-label reporting, and an executive partner.',
    features: [
      'Unlimited channels',
      'Custom AI workflows',
      'Dedicated pod (4+ specialists)',
      'White-label reporting',
      'SSO, SOC2, MSA',
      'Quarterly executive review',
    ],
    cta: 'Talk to sales',
    highlighted: false,
  },
];

export default function Pricing() {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Default-active plan is the highlighted one (the $500 Growth tier at index 1).
  const defaultIndex = tiers.findIndex((t) => t.highlighted);
  const [activeIndex, setActiveIndex] = useState(
    defaultIndex === -1 ? 0 : defaultIndex,
  );

  // Snap the carousel to a specific card index, centering it in the track.
  const scrollToIndex = (idx: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.querySelector<HTMLElement>(
      `[data-card-index="${idx}"]`,
    );
    if (!card) return;
    const target =
      card.offsetLeft - (container.clientWidth - card.clientWidth) / 2;
    container.scrollTo({ left: target, behavior: 'smooth' });
  };

  // On mount: jump (no animation) to the highlighted card so users land on the
  // recommended $500 plan first.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const card = container.querySelector<HTMLElement>(
      `[data-card-index="${defaultIndex}"]`,
    );
    if (!card) return;
    container.scrollLeft =
      card.offsetLeft - (container.clientWidth - card.clientWidth) / 2;
  }, [defaultIndex]);

  // Track which card is closest to the viewport center as the user scrolls /
  // swipes, so the dot indicator + active styling follow naturally.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = container.scrollLeft + container.clientWidth / 2;
        const cards =
          container.querySelectorAll<HTMLElement>('[data-card-index]');
        let closest = 0;
        let minDist = Infinity;
        cards.forEach((c) => {
          const childCenter = c.offsetLeft + c.clientWidth / 2;
          const dist = Math.abs(center - childCenter);
          if (dist < minDist) {
            minDist = dist;
            closest = parseInt(c.dataset.cardIndex || '0', 10);
          }
        });
        setActiveIndex(closest);
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="pricing"
      className="relative bg-background text-foreground py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-8 md:px-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              Pricing
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-medium tracking-[-1.5px] leading-[1.05]">
            One platform.{' '}
            <span className="font-serif italic font-normal">
              Three ways to grow.
            </span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground">
            All plans include onboarding, brand training, and the SprynAI AI
            engine. Cancel any time.
          </p>
        </motion.div>
      </div>

      {/* MOBILE carousel — sits OUTSIDE the bounded max-w-6xl container so cards
          can scroll edge-to-edge while still centering. Hidden on md+. */}
      <div className="md:hidden relative mx-auto max-w-7xl">
        {/* Scrollable track. Padding on each side equals (container - card)/2 so
            that the FIRST and LAST cards can snap to the viewport center. */}
        <div
          ref={scrollRef}
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pt-8 pb-8 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            paddingInline: 'max(2rem, calc(50% - 210px))',
            scrollPaddingInline: 'max(2rem, calc(50% - 210px))',
          }}
        >
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              data-card-index={i}
              animate={{
                // The currently-snapped card sits at full opacity + scale; the
                // others dim slightly so the eye lands on the active plan.
                // NOTE: `whileInView` was removed because it overrode `animate`
                // while the carousel was on screen, freezing every card at the
                // default state and breaking the active-card pop.
                opacity: i === activeIndex ? 1 : 0.6,
                scale: i === activeIndex ? 1 : 0.97,
              }}
              transition={{ type: 'spring', stiffness: 260, damping: 28 }}
              className={
                'snap-center flex-shrink-0 w-[88vw] max-w-[420px] relative rounded-2xl p-7 flex flex-col transform-gpu transition-colors ' +
                (t.highlighted
                  ? 'border border-white/30 bg-white/[0.04] shadow-[0_0_60px_-20px_rgba(255,255,255,0.25)]'
                  : 'border border-border bg-card')
              }
            >
              {t.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10.5px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}
              <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t.name}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-medium tracking-[-1.5px]">
                  {t.price}
                </span>
                <span className="text-muted-foreground">{t.cadence}</span>
              </div>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t.blurb}
              </p>

              <ul className="mt-7 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-foreground/85"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-white/[0.04]">
                      <Check className="h-3 w-3" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={
                  'mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-opacity ' +
                  (t.highlighted
                    ? 'bg-foreground text-background hover:opacity-90'
                    : 'border border-border text-foreground hover:bg-white/[0.04]')
                }
              >
                {t.cta}
              </a>
            </motion.div>
          ))}
        </div>
      </div>

      {/* MOBILE pill-style indicator: the active dot stretches into a bar;
          non-active dots stay small. Each is also a clickable jump-to-plan
          button. Hidden on md+ since desktop shows all 3 cards at once. */}
      <div className="md:hidden mt-2 flex justify-center gap-2.5">
        {tiers.map((t, i) => (
          <button
            key={t.name}
            type="button"
            onClick={() => scrollToIndex(i)}
            aria-label={`Show ${t.name} plan`}
            className={
              'h-1.5 rounded-full transition-all duration-300 ' +
              (i === activeIndex
                ? 'w-8 bg-foreground'
                : 'w-1.5 bg-foreground/30 hover:bg-foreground/50')
            }
          />
        ))}
      </div>

      {/* DESKTOP grid — 3-column layout, $500 plan highlighted.
          Container widened from `max-w-6xl px-28` (effective 928 px → ~293 px per card)
          to `max-w-7xl px-8` (effective ~1216 px → ~389 px per card) so cards aren't
          crammed. Card padding bumped p-8 → p-10 + gap-6 → gap-8 for breathing room. */}
      <div className="hidden md:block max-w-7xl mx-auto px-8">
        <div className="grid md:grid-cols-3 gap-8">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className={
                'relative rounded-2xl p-10 flex flex-col transition-colors ' +
                (t.highlighted
                  ? 'border border-white/30 bg-white/[0.04] shadow-[0_0_60px_-20px_rgba(255,255,255,0.25)] md:scale-[1.03]'
                  : 'border border-border bg-card hover:border-white/15')
              }
            >
              {t.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10.5px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}
              <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                {t.name}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-medium tracking-[-1.5px]">
                  {t.price}
                </span>
                <span className="text-muted-foreground">{t.cadence}</span>
              </div>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {t.blurb}
              </p>

              <ul className="mt-7 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-foreground/85"
                  >
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-white/[0.04]">
                      <Check className="h-3 w-3" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className={
                  'mt-8 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-opacity ' +
                  (t.highlighted
                    ? 'bg-foreground text-background hover:opacity-90'
                    : 'border border-border text-foreground hover:bg-white/[0.04]')
                }
              >
                {t.cta}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
